import type { TrainingBlock } from './protocolTypes'
import type { WorkoutAdjustments } from './dailyOverrideTypes'
import { parseSetCount } from './sessionProgress'

function adjustPrescriptionVolume(prescription: string, volumePct: number): string {
  if (volumePct >= 0) return prescription
  const factor = Math.max(0.5, 1 + volumePct / 100)
  const match = prescription.match(/(\d+)\s*x/i)
  if (!match) return prescription
  const sets = Math.max(1, Math.round(Number(match[1]) * factor))
  return prescription.replace(/(\d+)\s*x/i, `${sets} x`)
}

export type AppliedWorkoutContext = {
  block: TrainingBlock
  forceRestDay: boolean
  coachNote: string | null
  volumePct: number | null
  intensity: string | null
}

function applyExerciseSubstitutions(
  block: TrainingBlock,
  substitutions: WorkoutAdjustments['exercise_substitutions'],
): TrainingBlock {
  if (!substitutions?.length) return block

  const byOriginal = new Map(
    substitutions.map((s) => [s.original_name.toLowerCase(), s]),
  )

  return {
    ...block,
    exercises: block.exercises.map((ex) => {
      const sub = byOriginal.get(ex.exercise_name.toLowerCase())
      if (!sub) return ex
      return {
        ...ex,
        exercise_name: sub.substitute_name,
        prescription_text: sub.substitute_prescription,
        form_instructions: sub.reason,
      }
    }),
  }
}

export function applyWorkoutAdjustments(
  block: TrainingBlock,
  adjustments: WorkoutAdjustments | null | undefined,
): AppliedWorkoutContext {
  if (!adjustments || Object.keys(adjustments).length === 0) {
    return {
      block,
      forceRestDay: false,
      coachNote: null,
      volumePct: null,
      intensity: null,
    }
  }

  const volumePct = adjustments.workout_volume_adjustment_pct ?? null
  const forceRestDay = Boolean(adjustments.rest_day)

  if (forceRestDay) {
    return {
      block: {
        ...block,
        block_title: 'Recovery Day (Coach Adjusted)',
        focus_subtitle: adjustments.intensity ?? 'Rest and mobility',
        coach_why_rationale: adjustments.coach_note ?? block.coach_why_rationale,
        window_type: 'recovery',
      },
      forceRestDay: true,
      coachNote: adjustments.coach_note ?? null,
      volumePct,
      intensity: adjustments.intensity ?? null,
    }
  }

  let workingBlock = applyExerciseSubstitutions(block, adjustments.exercise_substitutions)

  const adjustedExercises = workingBlock.exercises.map((ex) => {
    if (volumePct == null || volumePct >= 0) return ex
    return {
      ...ex,
      prescription_text: adjustPrescriptionVolume(ex.prescription_text, volumePct),
    }
  })

  const subSuffix = adjustments.exercise_substitutions?.length
    ? ` · ${adjustments.exercise_substitutions.length} injury-aware swap(s)`
    : ''
  const suffix = volumePct != null && volumePct < 0 ? ` · Volume ${volumePct}%` : ''

  return {
    block: {
      ...workingBlock,
      focus_subtitle: `${workingBlock.focus_subtitle}${suffix}${subSuffix}`.trim(),
      coach_why_rationale: adjustments.coach_note ?? workingBlock.coach_why_rationale,
      exercises: adjustedExercises,
    },
    forceRestDay: false,
    coachNote: adjustments.coach_note ?? null,
    volumePct,
    intensity: adjustments.intensity ?? null,
  }
}

export function coachSignalWithAdjustments(
  logged: number,
  target: number,
  ctx: AppliedWorkoutContext,
  cycleReduced: boolean,
  cyclePhase: string | null,
): string {
  if (ctx.coachNote) return ctx.coachNote
  if (ctx.forceRestDay) return 'Recovery day — mobility, walking, and sleep take priority.'
  if (ctx.volumePct != null && ctx.volumePct < 0) {
    return `Wearable-adjusted volume (${ctx.volumePct}%). ${logged < target ? 'Prioritize form over load.' : 'Controlled effort — extended rest.'}`
  }
  if (cycleReduced) {
    return `Cycle-adapted session: reduce volume 10–15%. ${logged < target ? 'Prioritize form over load.' : 'Controlled effort — extended rest.'}`
  }
  if (cyclePhase === 'ovulatory') {
    return 'Peak force window — monitor joint laxity and avoid max-effort bouncing.'
  }
  return logged < target ? 'One more quality rep.' : 'Strong set — rest 90s.'
}

export { parseSetCount }
